import { Injectable } from '@angular/core';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { ProjectTaskModel } from '../../../../../core/models/project/project-task.model';
import { UserModel } from '../../../../../core/models/user/user.model';
import { StatusModel } from '../../../../../core/models/project/status.model';
import { ProjectDataService } from '../../state-and-data-handling/project-data.service';
import { ProjectExpenditureBudgetTransformerService } from '../manage-project/project-calculation-form/services/project-expenditure-budget-transformer.service';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../core/constants-repository/project-calculation.constants';
import { assert } from '../../../../../core/assert/assert';
import { UserService } from '../../../../user/services/user.service';
import { ProjectTaskGridRow } from '../../../shared/project-detail-types';
import { ExpenditureRateEntry } from '../../../../../core/models/project/project-expenditure-entry.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectTaskService {
  constructor(
    private readonly projectDataService: ProjectDataService,
    private readonly projectExpenditureBudgetService: ProjectExpenditureBudgetTransformerService,
  ) {}

  public createTask(
    id: number,
    taskName: string,
    status: StatusModel,
    assignee: UserModel,
    billable: boolean,
  ): ProjectTaskModel {
    const newTask = new ProjectTaskModel();
    newTask.id = id;
    newTask.name = taskName;
    newTask.assignee = assignee;
    newTask.status = status;
    newTask.isBillable = billable;
    return newTask;
  }

  public resetExpenditure(gridData: ProjectTaskGridRow[]): void {
    gridData.forEach((row, index) => {
      row.expenditureRateHistory = [
        {
          id: index,
          startingFrom: this.projectDataService.getProjectClone().startsAt,
          rate: 0,
        },
      ];
    });
  }

  public resetBudget(gridData: ProjectTaskGridRow[]): void {
    gridData.forEach((row) => {
      row.budgetSeconds = 0;
      row.budgetMonetary = 0;
    });
  }

  public syncProjectModel(gridData: ProjectTaskGridRow[], projectClone: ProjectModel): void {
    const monetaryBudgetPerTask = projectClone.budgetType === BudgetOptions.MonetaryPerTask;
    const [expenditureEntries, budgetEntries] =
      this.projectExpenditureBudgetService.transformTaskGridDataToExpenditureAndBudgetEntries(
        gridData,
        monetaryBudgetPerTask,
      );
    if (projectClone.expenditureType === ExpenditureOptions.HourlyRatePerTask) {
      projectClone.projectExpenditureEntries = expenditureEntries;
    }

    if (
      projectClone.budgetType === BudgetOptions.HoursPerTask ||
      projectClone.budgetType === BudgetOptions.MonetaryPerTask
    ) {
      projectClone.projectBudgetEntries = budgetEntries;
    }

    projectClone.tasks = gridData.map((row) => row.task);
    this.projectDataService.updateProject(projectClone);
  }

  public setHistoryForTask(
    gridData: ProjectTaskGridRow[],
    taskId: number,
    expenditureRateHistory: ExpenditureRateEntry[],
  ): void {
    const idx = gridData.findIndex((row) => row.task.id === taskId);
    const old = gridData[idx];
    assert(old !== undefined);

    const clone: ProjectTaskGridRow = { ...old };
    clone.expenditureRateHistory = expenditureRateHistory;
    gridData.splice(idx, 1, clone);
  }

  public setUnassignedUserForRemovedAssignee(
    gridData: ProjectTaskGridRow[],
    assigneeId: number,
  ): void {
    if (assigneeId === null) {
      return;
    }
    const unassignedUserModel = UserService.buildDefaultUser();
    const taskGridRows = gridData.filter((x) => x.task.assignee.id === assigneeId);
    taskGridRows.forEach((taskGridRow) => (taskGridRow.task.assignee = unassignedUserModel));

    this.syncProjectModel(gridData, this.projectDataService.getProjectClone());
  }
}
